import React, { useCallback, useMemo, useState } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';

// ECDEX-3840 (slice 1): content keys for banner incidents that come from the
// report being viewed (county court delays), not from the status page.
// ReportPage sets them and Header reads them. Replaces the
// apiStatusBanner.customIncidentContentKeys Redux state. Like that state, the
// keys are replaced by the next set and stay until the provider unmounts.
// Components read one piece through a selector, so ReportPage (which only sets
// the keys) does not re-render when they change.
interface CustomIncidentContextValue {
  customIncidentContentKeys: string[];
  addCustomIncident: (keys: string[]) => void;
}

const noKeys: string[] = [];

const CustomIncidentContext = createContext<CustomIncidentContextValue>({
  customIncidentContentKeys: noKeys,
  addCustomIncident: () => {},
});

export const CustomIncidentProvider: React.FC<{
  children?: React.ReactNode;
  initialKeys?: string[];
}> = ({ children, initialKeys = noKeys }) => {
  const [customIncidentContentKeys, setCustomIncidentContentKeys] =
    useState<string[]>(initialKeys);
  const addCustomIncident = useCallback(
    (keys: string[]) => setCustomIncidentContentKeys(keys),
    [],
  );
  const value = useMemo(
    () => ({ customIncidentContentKeys, addCustomIncident }),
    [customIncidentContentKeys, addCustomIncident],
  );
  return (
    <CustomIncidentContext.Provider value={value}>
      {children}
    </CustomIncidentContext.Provider>
  );
};

export const useCustomIncidentKeys = () =>
  useContextSelector(
    CustomIncidentContext,
    value => value.customIncidentContentKeys,
  );

export const useAddCustomIncident = () =>
  useContextSelector(CustomIncidentContext, value => value.addCustomIncident);
